<div class="detail-container" [class.compact]="compactMode">
  <!-- Header (hidden in compact mode — thread has its own back button) -->
  <div class="detail-header" *ngIf="!compactMode">
    <div class="getBack" (click)="onClose()" style="cursor:pointer; margin-bottom:0;">
      <i class="icon-arrow-left"></i>
      <span>{{ "buttons.back" | translate }}</span>
    </div>

    <div class="detail-actions" *ngIf="!isSentMessage">
      <button class="btn btn-hover btn-sm" (click)="toggleReply(false)" [title]="'communication.inbox.reply' | translate">
        <i class="icon-reply"></i>
      </button>
      <button class="btn btn-hover btn-sm" (click)="toggleReply(true)" [title]="'communication.inbox.reply_all' | translate">
        <i class="icon-users"></i>
      </button>
    </div>

    <div class="detail-actions" *ngIf="isSentMessage">
      <span class="badge bg-soft-sent">
        <i class="icon-paper-plane"></i> {{ "communication.inbox.sent" | translate }}
      </span>
    </div>
  </div>

  <!-- Scrollable content area (hidden in compact mode — thread shows the body) -->
  <div class="detail-scroll-area" *ngIf="!compactMode">
    <!-- Subject -->
    <div class="detail-subject">
      <h5>{{ message.subject || '(' + ("communication.inbox.no_subject" | translate) + ')' }}</h5>
    </div>

    <!-- From/To -->
    <div class="detail-meta">
      <div class="sender-row">
        <div class="sender-avatar">
          {{ fromDisplay.charAt(0).toUpperCase() }}
        </div>
        <div class="sender-info">
          <div class="sender-name">
            <strong>{{ fromDisplay }}</strong>
            <span class="sender-address">&lt;{{ fromEmail }}&gt;</span>
          </div>
          <div class="sender-to">
            {{ "communication.inbox.to" | translate }}: {{ toDisplay }}
            <span *ngIf="ccDisplay">, CC: {{ ccDisplay }}</span>
          </div>
        </div>
        <div class="detail-date">
          {{ formatDate(message.date) }}
        </div>
      </div>
    </div>

    <!-- Attachments -->
    <div class="detail-attachments" *ngIf="message.attachments && message.attachments.length > 0">
      <div class="attachments-header">
        <i class="icon-paperclip"></i>
        {{ message.attachments.length }} {{ "communication.inbox.attachments" | translate }}
      </div>
      <div class="attachments-list">
        <div class="attachment-item" *ngFor="let att of message.attachments">
          <i [class]="getFileIcon(att.mime)"></i>
          <div class="attachment-info">
            <span class="attachment-name">{{ att.name }}</span>
            <span class="attachment-size">{{ formatFileSize(att.size) }}</span>
          </div>
          <button class="btn btn-hover btn-sm" (click)="downloadAttachment(att)"
            [disabled]="downloadingIndex === att.index"
            [title]="'communication.inbox.download' | translate">
            <i [class]="downloadingIndex === att.index ? 'spinner-border spinner-border-sm' : 'icon-download2'"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="detail-body" [innerHTML]="sanitizedBody"></div>
  </div>

  <!-- Reply form — only for inbox messages -->
  <div class="reply-form" [class.reply-form-open]="showReplyForm" [class.reply-form-closed]="!showReplyForm"
    *ngIf="(showReplyForm || compactMode) && !isSentMessage">

    <!-- Compact mode: just show reply button when closed -->
    <div class="compact-reply-header" *ngIf="compactMode && !showReplyForm">
      <button class="btn btn-hover btn-sm" (click)="toggleReply(false)">
        <i class="icon-reply"></i> {{ "communication.inbox.reply" | translate }}
      </button>
    </div>

    <!-- Reply header (non-compact) -->
    <div class="reply-header" *ngIf="showReplyForm && !compactMode">
      <i class="icon-reply"></i>
      <span *ngIf="!isReplyAll">{{ "communication.inbox.reply_to" | translate }} {{ fromDisplay }}</span>
      <span *ngIf="isReplyAll">{{ "communication.inbox.reply_all_to" | translate }} {{ fromDisplay }}, {{ replyAllRecipients }}</span>
    </div>

    <ng-container *ngIf="showReplyForm">
      <!-- Rich text editor -->
      <div class="richtext-editor">
        <div class="richtext-toolbar">
          <button type="button" (click)="execCommand('bold')" title="Negrita"><b>B</b></button>
          <button type="button" (click)="execCommand('italic')" title="Cursiva"><i>I</i></button>
          <button type="button" (click)="execCommand('underline')" title="Subrayado"><u>U</u></button>
          <span class="toolbar-separator"></span>
          <button type="button" (click)="execCommand('justifyLeft')" title="Izquierda"><i class="icon-align-left"></i></button>
          <button type="button" (click)="execCommand('justifyCenter')" title="Centro"><i class="icon-align-center"></i></button>
          <button type="button" (click)="execCommand('justifyRight')" title="Derecha"><i class="icon-align-right"></i></button>
          <span class="toolbar-separator"></span>
          <button type="button" (click)="execCommand('insertUnorderedList')" title="Lista">&#8226;</button>
          <button type="button" (click)="insertLink()" title="Link"><i class="icon-link"></i></button>
          <div class="toolbar-spacer"></div>
          <!-- Template selector button (opens Swal) -->
          <button type="button" class="toolbar-template-btn" (click)="openTemplateSelector()"
            [title]="'communication.inbox.use_template' | translate">
            <i class="icon-file-text"></i>
            <span class="template-btn-label">{{ "communication.inbox.template" | translate }}</span>
          </button>
        </div>

        <div class="richtext-content" contenteditable="true"
          #replyEditor
          (input)="onEditorInput($event)"
          (blur)="onEditorInput($event)"
          [attr.data-placeholder]="'communication.inbox.reply_placeholder' | translate">
        </div>
      </div>

      <!-- Reply attachments -->
      <div class="reply-attachments" *ngIf="replyAttachments.length > 0">
        <div class="reply-attachment-item" *ngFor="let file of replyAttachments; let i = index">
          <i class="icon-paperclip"></i>
          <span class="reply-att-name">{{ file.name }}</span>
          <span class="reply-att-size">{{ formatFileSize(file.size) }}</span>
          <button class="btn btn-hover btn-sm" (click)="removeAttachment(i)">
            <i class="icon-cross"></i>
          </button>
        </div>
      </div>

      <div class="reply-actions">
        <button class="btn btn-primary btn-sm" (click)="sendReply()" [disabled]="isSending">
          <i [class]="isSending ? 'spinner-border spinner-border-sm' : 'icon-paper-plane'"></i>
          {{ "communication.inbox.send" | translate }}
        </button>
        <button class="btn btn-hover btn-sm" (click)="fileInput.click()" [title]="'communication.inbox.attach_files' | translate">
          <i class="icon-paperclip"></i>
        </button>
        <input #fileInput type="file" multiple style="display:none" (change)="onFilesSelected($event)">
        <button class="btn btn-sm btn-cancel-notes" (click)="toggleReply()">
          {{ "buttons.cancel" | translate }}
        </button>
      </div>
    </ng-container>
  </div>
</div>
